/**
 * Boho painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const bohoPaintingIdeasPack: ToilePack = {
    slug: 'boho-painting-ideas',
    category: 'craft',
    name: 'Boho painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'rainbow', label: 'Boho rainbow', subject: 'a canvas of a boho rainbow of terracotta, mustard and blush arches on cream'},
        {key: 'pampas', label: 'Pampas grass', subject: 'a canvas of pampas grass in a round vase, the plumes painted in soft dry strokes'},
        {key: 'dunes', label: 'Sun over dunes', subject: 'a canvas of a half sun with ray lines over layered desert dunes'},
    ],
    styles: [
        {key: 'blocks', label: 'Flat blocks', style: 'flat mid-century boho acrylic, smooth matte blocks of color with crisp edges', palette: 'terracotta, mustard, blush and sand', framing: 'the canvas seen straight on, filling most of the frame, a strip of white wall around it'},
        {key: 'sandy', label: 'Sand texture', style: 'gritty textured acrylic mixed with sand, visible knife marks and raised edges', palette: 'rust, burnt orange, olive and bone', framing: 'the canvas hung above a rattan bench with a woven cushion and a trailing plant'},
        {key: 'oneline', label: 'One line', style: 'minimal art of one continuous thin brown painted line with a few flat color shapes', palette: 'earthy brown, cream, dusty rose and ochre', framing: 'the canvas leaning against a wall on a jute rug beside a clay pot'},
    ],
    directions: ['a cactus', 'black and gold', 'for a nursery'],
    directionHint: 'try: a cactus',
    seo: {
        title: 'Boho Painting Ideas: Arches, Pampas Grass, Dunes',
        metaDescription: 'Nine boho canvas ideas in desert colors: rainbow arches, pampas grass and a sun over dunes, in flat, sandy and one-line styles.',
        h1: 'Boho painting ideas: rainbow arches, pampas grass, sun over dunes',
        tagline: 'Nine finished boho canvases in terracotta, mustard, blush and sand, three subjects in three finishes.',
        intro: 'Boho canvases bring the desert and the dried-flower palette indoors: terracotta, mustard, blush and sand. These nine show three subjects: a rainbow of arches in terracotta, mustard and blush on cream, pampas grass plumes in a round vase, and a half sun with ray lines over layered dunes. Type another subject, such as a cactus or a nursery theme, and the same palette carries it.',
        useCases: [
            'a living room wall in warm tones',
            'a nursery picture',
            'a corner with rattan and plants',
            'a housewarming canvas',
        ],
        faq: [
            {q: 'What are the boho colors?', a: 'Terracotta, mustard, blush, sand and rust, with earthy brown and cream.'},
            {q: 'How does the sandy finish differ?', a: 'It is gritty textured paint mixed with sand, with visible knife marks and raised edges.'},
            {q: 'What is the one-line finish?', a: 'One continuous thin brown line with a few flat color shapes, on a clean cream ground.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the boho palette and the three finishes carry it.'},
        ],
    },
}
